import i18next from 'i18next';
import { PieData, PieSeriesData } from 'components/Graph/PieGraph';
import portfolioColors from 'components/PortfolioTables/colors';
import {
  AccountEditPlan,
  BucketPieDataset,
  BucketType,
  ChartComputedData,
  ChartMetadata,
  Dataset,
  DatasetData,
  PortfolioCode,
  PortfolioType,
} from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { ChartData, ChartTooltipData } from 'components/Graph/ChartGraph';
import { compareChartDates, translatePortfolioCode, translatePortfolioType } from './helpers';
import colors from 'components/PortfolioTables/colors';
import { colorOpacity, colors as basicColors } from 'components/Bucket/Bucket';
import { ExtProdSortBy } from 'pages/Accounts/Edit/components/ExternalProducts/model';

import { ExternalProductData, FinancialInstitution } from 'pages/Accounts/Edit/components/ExternalProducts/model';
import { Currency, EdwardColors } from 'enums';
import { sortExtProducts } from 'pages/Accounts/Edit/components/ExternalProducts/ExternalProducts';

export function formatAxisAmmountValue(value: number | string, currency?: Currency): string {
  const valueStr = value.toLocaleString(i18next.language);
  if (currency) {
    return `${valueStr} ${currency}`;
  }
  return valueStr;
}

export function convertPiechartData(
  pieDataset: BucketPieDataset[],
  code: PortfolioCode,
  type?: BucketType,
): PieSeriesData {
  const pieData = pieDataset.map(
    (set: BucketPieDataset): PieData => ({
      name: set.title,
      y: Math.round(set.share * 100),
      color: portfolioColors[set.code],
    }),
  );
  const title = code.title || translatePortfolioCode(code.code);
  const subtitle = translatePortfolioType(code.code, true);
  const portfolioColor = code.type === PortfolioType.ESG ? basicColors['ESG'] : type ? basicColors[`${type}_TITLE`] : '';

  return { data: pieData, title, subtitle, portfolioColor };
}

export function convertCompositionPiechartData(
  pieDataset: { [id: string]: number }, // in ExtProductRegion || ExtProductAsset || Currency
  type: 'asset' | 'region' | 'currency' | 'finInst',
  finInst: { [id: string]: FinancialInstitution },
  original?: boolean,
): { name: string; y: number; color: string }[] {
  const pieData = Object.keys(pieDataset).map((key) => {
    let name = '';
    switch (type) {
      case 'finInst':
        name = key === 'PROPERTY' ? i18next.t(`externalProducts_title_property`) : finInst[key].title;
        break;
      case 'asset':
        name = i18next.t(`externalProduct_asset_${key}`);
        break;
      case 'region':
        name = i18next.t(`externalProduct_region_${key}`);
        break;
      case 'currency':
        name = i18next.t(`currency_${key}`);
        break;
    }

    return {
      name,
      y: Math.round(pieDataset[key] * 100),
      color:
        type === 'finInst'
          ? original
            ? grayscale(key === 'PROPERTY' ? EdwardColors.GRAY_LIGHT : finInst[key].colour!)
            : key === 'PROPERTY'
            ? EdwardColors.GRAY_LIGHT
            : finInst[key].colour!
          : original
          ? grayscale(portfolioColors[`${type.toUpperCase()}_${key}`])
          : portfolioColors[`${type.toUpperCase()}_${key}`],
    };
  });
  //const title = code.title || translatePortfolioCode(code.code);
  //const subtitle = translatePortfolioType(code.code, true);
  //const portfolioColor = code.type === PortfolioType.ESG ? colors['ESG'] : type ? colors[`${type}_TITLE`] : '';

  return pieData;
}

export function alterColor(color: string, v: number) {
  if (!color) return '#000'; // FIXME: hotfix

  const rgb = parseInt(color.substring(1, color.length), 16);

  let r = Math.abs(((rgb >> 16) & 0xff) + v);
  if (r > 255) r = r - (r - 255);
  let g = Math.abs(((rgb >> 8) & 0xff) + v);
  if (g > 255) g = g - (g - 255);
  let b = Math.abs((rgb & 0xff) + v);
  if (b > 255) b = b - (b - 255);

  let r2 = Number(r < 0 || isNaN(r)) ? '0' : (r > 255 ? 255 : r).toString(16);
  if (r2.length === 1) r2 = '0' + r;
  let g2 = Number(g < 0 || isNaN(g)) ? '0' : (g > 255 ? 255 : g).toString(16);
  if (g2.length === 1) g2 = '0' + g;
  let b2 = Number(b < 0 || isNaN(b)) ? '0' : (b > 255 ? 255 : b).toString(16);
  if (b2.length === 1) b2 = '0' + b;

  return '#' + r2 + g2 + b2;
}

export function grayscale(color?: string) {
  if (color) {
    const rgb = parseInt(color.substring(1, color.length), 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = rgb & 0xff;
    let avg = Math.round(((r / 255 + g / 255 + b / 255) / 3) * 255).toString(16);
    if (avg.length === 1) avg = '0' + avg;
    return `#${avg}${avg}${avg}`;
  } else {
    return '#000000';
  }
}

export function computeChartData(data: AccountEditPlan, chartData: ChartComputedData) {
  chartData.inflation = data.chart?.inflation?.enabled;
  chartData.inflationRate = data.chart?.inflation?.rate;
  //chartData.previewMin = data.chart.metadata?.min; // dashboard
  //chartData.previewMax = data.chart.metadata?.max; // dashboard
  chartData.endValue = data.chart.valueAtEnd?.value;
  chartData.displayDate = data.chart.displayDate;
  chartData.displayValue = data.chart.valueAtDisplay?.value;
  chartData.displayAge = data.chart.ageAtDisplay || null;
  chartData.showRange = data.rentApplied === false;
  /*
  if (!chartData.showRange) {
    delete data.chart.metadata?.min;
    delete data.chart.metadata?.max;
  }
  */
  chartData.displaySetValue = data.targetData.targets?.length > 0;
  chartData.totalIndex = data.chart.dataset!.findIndex((set) => set.title === 'total.title');
  chartData.ageIndex = data.chart.dataset!.findIndex((set) => set.title === 'client.age');
  chartData.endDate =
    chartData.showRange &&
    data.chart.metadata?.max &&
    compareChartDates(data.chart.metadata?.max, data.chart.endDate) === -1
      ? data.chart.metadata.max
      : data.chart.endDate;
  chartData.default = false;
}

export function chartDatasets(data: AccountEditPlan): Dataset[] {
  if (!data?.chart.dataset) {
    return [];
  }
  return data?.chart.dataset.map((ds) => {
    return { title: ds.title, data: Object.keys(ds.data).map((d) => [d, ds.data[d]]) };
  });
}

export function chartMetadata(data: AccountEditPlan): ChartMetadata {
  return (
    data.chart?.metadata || {
      rentStartIndex: 0,
      ageAxisStep: 1,
      partnerAgePresent: false,
    }
  );
}

export function chartTooltipData(
  data: AccountEditPlan,
  altColors?: boolean,
  extProducts?: ExternalProductData[],
): ChartTooltipData {
  if (!data || !data.chart) return {};

  const { dataset } = data.chart;
  const datasetsToIgnore = ['targets', 'deposits', 'loan'];
  //const datasetsNotSorted = ['bucket', 'client', 'total'];
  let tooltipData = {};
  if (dataset) {
    tooltipData = dataset
      .filter((single) => {
        const key = single.title.split('.')[0];
        const extProd = extProducts && single.title.substr(0, 15) === 'externalProduct';
        const property = extProducts && single.title.substr(0, 8) === 'property';
        const extProdId = extProd || property ? single.title.substr(extProd ? 16 : 9) : undefined;
        const extProdData = extProdId ? extProducts!.find((prod) => prod.id === extProdId) : undefined;

        return datasetsToIgnore.indexOf(key) === -1 && (!extProdData || extProdData.includedInChart);
      })
      .sort((a, b) => {
        const keyIdA = a.title.split('.');
        const extProdIdA = keyIdA[1] || undefined;
        const extProdDataA = extProdIdA ? extProducts!.find((prod) => prod.id === extProdIdA) : undefined;
        const keyIdB = b.title.split('.');
        const extProdIdB = keyIdB[1] || undefined;
        const extProdDataB = extProdIdB ? extProducts!.find((prod) => prod.id === extProdIdB) : undefined;

        return extProdDataA && extProdDataB ? sortExtProducts(extProdDataA, extProdDataB, ExtProdSortBy.INST, true) : 0;
      })
      .reduce((previous: ChartTooltipData, single): ChartTooltipData => {
        // FIXME: c&p next method + PDF
        const extProd = extProducts && single.title.substr(0, 15) === 'externalProduct';
        const property = extProducts && single.title.substr(0, 8) === 'property';
        const loan = extProducts && single.title.substr(0, 4) === 'loan';

        const extProdId =
          extProd || property || loan ? single.title.substr(extProd ? 16 : property ? 9 : 5) : undefined;

        const extProdData = extProdId ? extProducts!.find((prod) => prod.id === extProdId) : undefined;
        const extProdInst = extProdData ? extProdData.financialInstitution?.title : undefined;
        const extProdTitle = extProdData ? extProdData.title : undefined;

        const extProdName =
          extProdInst && extProdTitle ? `${extProdInst} - ${extProdTitle}` : extProdTitle ? extProdTitle : undefined;
        const extProdColor = extProd
          ? extProdData?.financialInstitution?.colour
          : property
          ? colors[`PROPERTY_TYPE_${extProdData?.type}`]
          : loan
          ? EdwardColors.GOLD
          : undefined;

        previous[single.title] = {
          title: extProdData ? extProdName : undefined,
          data: single.data,
          detailData: single.detailData,
          color: extProdColor
            ? altColors
              ? grayscale(extProdColor)
              : extProdColor
            : basicColors[`${single.title.split('.')[1].toUpperCase()}${altColors ? '_ALT' : ''}`],
        };
        return previous;
      }, {});
  }

  return tooltipData;
}

export function chart(data: AccountEditPlan, altColors?: boolean, extProducts?: ExternalProductData[]): ChartData[] {
  const datasets = chartDatasets(data);
  const datasetsToIgnore = ['total.title', 'client.age', 'targets', 'deposits'];
  return datasets
    .filter((single: Dataset) => datasetsToIgnore.indexOf(single.title) === -1)
    .map((single: Dataset, i): ChartData => {
      const extProd = extProducts && single.title.substr(0, 15) === 'externalProduct';
      const property = extProducts && single.title.substr(0, 8) === 'property';
      const loan = extProducts && single.title.substr(0, 4) === 'loan';

      const extProdId = extProd || property || loan ? single.title.substr(extProd ? 16 : property ? 9 : 5) : undefined;

      const extProdData = extProdId ? extProducts!.find((prod) => prod.id === extProdId) : undefined;
      const extProdInst = extProdData ? extProdData.financialInstitution?.title : undefined;
      const extProdTitle = extProdData ? extProdData.title : undefined;
      const extProdName =
        extProdInst && extProdTitle
          ? extProdInst + `<span>${extProdTitle.length > 15 ? extProdTitle.substr(0, 15) + '…' : extProdTitle}</span>`
          : extProdTitle
          ? extProdTitle
          : undefined;
      const extProdColor = extProd
        ? extProdData?.financialInstitution?.colour
        : property
        ? colors[`PROPERTY_TYPE_${extProdData?.type}`]
        : loan
        ? EdwardColors.GOLD
        : undefined;

      const dataset: ChartData = {
        name: extProdName || i18next.t(`${single.title}${data?.portfolioOnly ? '_portfolioOnly' : ''}`),
        data: loan ? [] : single.data,
        //data: single.data,
        type: 'area',
        color: extProdColor
          ? altColors
            ? grayscale(extProdColor)
            : extProdColor
          : basicColors[`${single.title.split('.')[1].toUpperCase()}${altColors ? '_ALT' : ''}`],
        fillOpacity: extProdData ? 0.85 : colorOpacity[`${single.title.split('.')[1].toUpperCase()}_OPACITY`],
        id: extProdId,
        visible: extProdData ? !!extProdData.includedInChart : undefined,
      };
      if (!extProdId) delete dataset.visible;
      return dataset;
    });
}

export function chartDataDates(data: AccountEditPlan): string[] {
  const chartData = chart(data);
  const dataArray = chartData[0].data as DatasetData;
  return dataArray.map(([date]) => date);
}

export function total(data: AccountEditPlan): any[] {
  const datasets = chartDatasets(data);
  return datasets.find((set: Dataset) => set.title === 'total.title')?.data || [];
}
